import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/feature/AppShell";
import ProgressBar from "@/components/base/ProgressBar";
import {
  fetchQuizQuestions,
  submitQuizAnswers,
  type QuizQuestion,
  type QuizOption,
} from "@/services/quiz";

export default function Test() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<Record<string, QuizOption>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    fetchQuizQuestions()
      .then((qs) => {
        if (active) setQuestions(qs);
      })
      .catch((err) => {
        if (active) setLoadError(err instanceof Error ? err.message : "No se pudo cargar el test.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const total = questions.length;
  const question = questions[current];
  const percentComplete = total ? Math.round(((current + 1) / total) * 100) : 0;
  const selectedOption = question ? answers[question.id] : undefined;
  const isLast = current === total - 1;

  const answeredCount = useMemo(() => Object.keys(answers).length, [answers]);

  const handleSelect = (opt: QuizOption) => {
    if (!question) return;
    setAnswers((prev) => ({ ...prev, [question.id]: opt }));
  };

  const handleNext = async () => {
    if (!question || !selectedOption) return;
    if (!isLast) {
      setCurrent((c) => c + 1);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitQuizAnswers(answers);
      navigate("/app/resultado");
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "No se pudo guardar tu resultado.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrev = () => {
    if (current > 0) setCurrent((c) => c - 1);
  };

  if (loading) {
    return (
      <AppShell headerVariant="onLight" showBack>
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-16 text-center text-foreground-500">
          Cargando tu test...
        </div>
      </AppShell>
    );
  }

  if (loadError || !question) {
    return (
      <AppShell headerVariant="onLight" showBack>
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-16 text-center text-foreground-500">
          {loadError ?? "El test todavía no está disponible."}
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell headerVariant="onLight" showBack>
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-semibold text-foreground-950">
              Pregunta <span className="text-primary-600">{current + 1}</span> de {total}
            </div>
          </div>
          <button
            onClick={() => navigate("/app/dashboard")}
            className="inline-flex items-center gap-2 border border-background-300 rounded-full px-4 py-2 text-xs text-foreground-600 hover:border-accent-400 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line"></i> Salir del test
          </button>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1"><ProgressBar value={percentComplete} color="accent" size="md" /></div>
          <div className="text-xs text-foreground-500 whitespace-nowrap">{percentComplete}% completado</div>
        </div>

        <div className="bg-accent-500/10 border border-accent-500/25 rounded-xl p-4 flex items-start gap-3 mb-5">
          <span className="w-8 h-8 rounded-full bg-accent-500/20 text-accent-600 flex items-center justify-center flex-shrink-0">
            <i className="ri-sparkling-2-fill"></i>
          </span>
          <p className="text-sm text-foreground-800">
            No hay respuestas correctas o incorrectas. Sé tú mismo(a), así obtendrás mejores resultados.
          </p>
        </div>

        <div className="bg-background-100 border border-background-200 rounded-2xl p-5 md:p-6">
          <div className="flex items-start gap-3 mb-5">
            <span className="w-8 h-8 rounded-lg bg-primary-500/15 text-primary-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-question-line"></i>
            </span>
            <h2 className="font-heading font-bold text-lg text-foreground-950">{question.text}</h2>
          </div>

          <div className="space-y-3">
            {question.options.map((opt) => {
              const isSelected = selectedOption?.key === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => handleSelect(opt)}
                  className={`w-full flex items-center gap-4 rounded-xl border p-4 text-left cursor-pointer transition-colors ${
                    isSelected
                      ? "border-primary-500 bg-primary-500/5"
                      : "border-background-200 hover:border-background-300"
                  }`}
                >
                  <span className="w-8 h-8 rounded-full bg-background-200 text-foreground-700 flex items-center justify-center font-heading font-bold text-sm flex-shrink-0">
                    {opt.key}
                  </span>
                  <span className="flex-1">
                    <span className="block font-semibold text-sm text-foreground-950">{opt.label}</span>
                    <span className="block text-xs text-foreground-500 mt-0.5">{opt.sub}</span>
                  </span>
                  <i className={`${opt.icon} text-xl text-foreground-400 flex-shrink-0`}></i>
                  {isSelected ? (
                    <span className="w-6 h-6 rounded-full bg-primary-500 text-white flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-sm"></i>
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-5 bg-background-900 text-background-50 rounded-xl p-4 flex items-start gap-3">
          <span className="w-8 h-8 rounded-full bg-background-800 flex items-center justify-center flex-shrink-0">
            <i className="ri-robot-2-line text-primary-300"></i>
          </span>
          <p className="text-sm text-foreground-200">
            Cada respuesta nos ayuda a entender tu forma de pensar, sentir y actuar. Así creamos tu ruta personalizada.
          </p>
        </div>

        {submitError ? <p className="mt-3 text-sm text-red-600">{submitError}</p> : null}

        <div className="mt-6 flex items-center justify-between gap-4">
          <button
            onClick={handlePrev}
            disabled={current === 0}
            className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-heading font-semibold border border-background-300 text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-background-400 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i> Anterior
          </button>
          <button
            onClick={handleNext}
            disabled={!selectedOption || submitting}
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-heading font-bold vv-gradient-brand text-background-950 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {submitting ? "Guardando..." : isLast ? "Ver mi resultado" : "Siguiente pregunta"}
            {!submitting ? <i className="ri-arrow-right-line"></i> : null}
          </button>
        </div>

        <div className="mt-3 text-center text-xs text-foreground-400">{answeredCount} de {total} respondidas</div>
      </div>
    </AppShell>
  );
}
